/* ----------------------------------------------------
              Modern boot (#1716)

  Turbo for transport, Stimulus for behavior.
  See AGENTS.md — Hotwire & Stimulus.
----------------------------------------------------- */

/* Framework */
import * as ActiveStorage from "@rails/activestorage";
import "trix";
import "@rails/actiontext";
import * as Turbo from "@hotwired/turbo-rails";
import posthog from "posthog-js";

ActiveStorage.start();

/* Stimulus */
import "../controllers/index";

/* Map managers (CDN loaders used by Stimulus map controllers) */
import "../components/mapbox_manager";

/* Analytics */
window.posthog = posthog;

let posthogInitialized = false;
let lastIdentifiedUserId = null;
let lastPlanroomId = null;
let lastCapturedPageviewUrl = null;

const posthogBodyConfig = () => {
  const body = document.body;

  return {
    enabled: body?.dataset.posthogEnabled === "true",
    localMode: body?.dataset.posthogLocalMode === "true",
    apiKey: body?.dataset.posthogApiKey,
    apiHost: body?.dataset.posthogApiHost,
    userId: body?.dataset.posthogUserId,
    userEmail: body?.dataset.posthogUserEmail,
    userName: body?.dataset.posthogUserName,
    userTrade: body?.dataset.posthogUserTrade,
    userCompany: body?.dataset.posthogUserCompany,
    planroomId: body?.dataset.posthogPlanroomId,
    planroomName: body?.dataset.posthogPlanroomName
  };
};

const initializePosthog = (config) => {
  if (posthogInitialized || !config.enabled || !config.apiKey) return;

  posthog.init(config.apiKey, {
    api_host: config.apiHost,
    defaults: "2026-01-30",
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: true,
    advanced_disable_flags: config.localMode,
    advanced_disable_feature_flags: config.localMode,
    disable_surveys: config.localMode,
    disable_conversations: config.localMode,
    disable_product_tours: config.localMode,
    request_batching: !config.localMode,
    disable_compression: config.localMode,
    debug: config.localMode,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-ph-mask]"
    }
  });

  posthogInitialized = true;
};

const syncPosthogUser = (config) => {
  if (!posthogInitialized) return;

  if (!config.userId) {
    if (lastIdentifiedUserId) {
      posthog.reset();
      posthogInitialized = false;
      lastIdentifiedUserId = null;
      lastPlanroomId = null;
      lastCapturedPageviewUrl = null;
      initializePosthog(config);
    }

    return;
  }

  const personProperties = {
    email: config.userEmail,
    name: config.userName,
    trade: config.userTrade,
    company: config.userCompany
  };

  if (lastIdentifiedUserId === config.userId) {
    posthog.setPersonProperties(personProperties);
    return;
  }

  posthog.identify(String(config.userId), personProperties);
  lastIdentifiedUserId = config.userId;
};

const syncPosthogPlanroom = (config) => {
  if (!posthogInitialized) return;

  if (!config.planroomId) {
    lastPlanroomId = null;
    return;
  }

  if (lastPlanroomId === config.planroomId) return;

  posthog.group("planroom", String(config.planroomId), {
    name: config.planroomName
  });
  lastPlanroomId = config.planroomId;
};

const captureTurboPageview = () => {
  if (!posthogInitialized) return;

  const currentUrl = window.location.href;
  if (lastCapturedPageviewUrl === currentUrl) return;

  posthog.capture("$pageview", {
    $current_url: currentUrl,
    path: window.location.pathname,
    search: window.location.search
  });
  lastCapturedPageviewUrl = currentUrl;
};

document.addEventListener("turbo:load", () => {
  const config = posthogBodyConfig();

  initializePosthog(config);
  syncPosthogUser(config);
  syncPosthogPlanroom(config);
  captureTurboPageview();
});
